import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { read } from '../api/client.js';
import { WriteBoundary } from '../api/write-boundary.js';
import { readActivityWindow } from './activity-window.js';
import { useResource } from '../api/resource.js';
import { useSession } from '../session/session.jsx';
import { Button, Dialog } from '../ui/primitives.jsx';
import { Resource } from '../ui/states.jsx';
import { InboxDecision } from './InboxDecision.jsx';
import './inbox.css';

export function activityLabel(event) {
  if (event.eventType === 'proposal.responded') return event.response === 'accept' ? '변경에 동의함' : '반대 의견을 남김';
  return ({
    'question.opened': '질문을 남김', 'question.answered': '질문에 답함', 'question.withdrawn': '질문을 거둠',
    'handoff.offered': '이어가기를 요청함', 'handoff.accepted': '이어가기로 함',
    'handoff.declined': '이어가기 어려움을 알림', 'handoff.returned': '결과를 돌려줌',
    'proposal.submitted': '공동 이해의 변경을 제안함', 'version.promoted': `공동 이해 v${event.versionNo}에 반영됨`,
  })[event.eventType] ?? '기록';
}

// These are Ledger records, not authored messages. The exact entity is reopened
// through the same decision view as Inbox; merely rendering never marks it seen.
export function ConversationActivity({ spaceId, threadId, members, archived }) {
  const session = useSession();
  const scope = `${session.nodeId}:${spaceId}:${threadId}`;
  const windowRef = useRef({ scope, oldest: null, more: false });
  if (windowRef.current.scope !== scope) windowRef.current = { scope, oldest: null, more: false };
  const [selected, select] = useState(null);
  useLayoutEffect(() => { select(null); }, [scope]);
  const trigger = useRef(null);
  const heading = useRef(null);
  const restoreFocus = useRef(false);
  const activity = useResource(`space:${spaceId}:conversation-activity:${threadId}`, async (signal) => {
    const data = await readActivityWindow((before) => read('conversation-activity', {
      nodeId: session.nodeId, query: { spaceId, threadId, ...(before ? { beforeCursor: before } : {}) }, signal,
    }), { ...windowRef.current, signal });
    windowRef.current = { scope, start: windowRef.current.start, oldest: data.oldest, more: false };
    return data;
  }, { quiet: true, identity: session.nodeId });
  const close = () => { restoreFocus.current = true; select(null); activity.reload(); };
  useEffect(() => {
    if (selected || !restoreFocus.current) return;
    restoreFocus.current = false;
    // A successful response invalidates/replaces the event list. Its old opener
    // may no longer exist; return to the stable section heading in that case.
    (trigger.current?.isConnected ? trigger.current : heading.current)?.focus();
  }, [selected]);
  const name = (id) => id === session.nodeId ? '나' : members?.find((m) => m.nodeId === id)?.displayName ?? '참여자';
  return <WriteBoundary resources={[activity]}><section class="conversation-activity" aria-label="이 대화에서 이어진 요청과 응답">
    <header class="row row--between">
      <h2 ref={heading} tabIndex={-1} class="t-body-strong">이 대화에서 이어진 요청·응답</h2>
      <Button size="xs" tone="ghost" onClick={() => { windowRef.current = { scope, oldest: null, more: false }; activity.reload(); }}>새로 확인</Button>
    </header>
    <Resource resource={activity} loadingLabel="요청과 응답을 확인하는 중">{(data) => <>
      {data.events?.length ? <ol class="conversation-activity__list">{[...data.events].reverse().map((event) => <li key={event.eventId}>
        <article class="conversation-activity__record">
          <p class="t-meta">{name(event.actorNodeId)} · {activityLabel(event)}
            {event.actorProvenance === 'delegated_ai' || event.actorProvenance === 'autonomous_ai' ? ' · AI' : ''}
          </p>
          <p class="t-body-strong">{event.taskTitle}</p>
          {event.requestText ? <p class="t-body">{event.requestText}</p> : null}
          {event.responseText ? <p class="t-body">{event.responseText}</p> : null}
          <div class="row row--between">
            <time class="t-meta" dateTime={event.occurredAt}>{new Date(event.occurredAt).toLocaleString('ko-KR')}</time>
            <Button size="xs" onClick={(e) => { trigger.current = e.currentTarget; select(event); }}>내용 확인·응답</Button>
          </div>
        </article>
      </li>)}</ol> : <p class="t-meta">아직 이 대화에서 이어진 요청이 없습니다.</p>}
      <div class="row">
        {data.incomplete ? <p class="t-meta">이 구간의 기록을 표시했습니다. 더 이전 기록은 다음 구간에서 확인할 수 있습니다.</p> : null}
        {data.hasMore ? <Button size="xs" disabled={activity.isStale} onClick={() => {
          windowRef.current = data.incomplete
            ? { scope, start: data.nextBeforeCursor, oldest: null, more: false }
            : { ...windowRef.current, more: true };
          activity.reload();
        }}>{data.incomplete ? '이전 구간 보기' : '이전 요청·응답'}</Button> : null}
        {windowRef.current.start ? <Button size="xs" disabled={activity.isStale} onClick={() => {
          windowRef.current = { scope, oldest: null, more: false }; activity.reload();
        }}>최근 기록으로</Button> : null}
      </div>
    </>}</Resource>
    {selected && activity.status === 'ready' ? <Dialog title="대화에서 이어진 요청" onClose={close}>
      <InboxDecision key={selected.eventId} pointer={selected} spaceId={spaceId} home={{ members }} archived={archived}
        closeLabel="대화로 돌아가기" close={close} />
    </Dialog> : null}
  </section></WriteBoundary>;
}
